/**
 * Application initialization service
 * Sets up all services and handles application lifecycle
 */

import dataLoadingService from './dataLoadingService.js';
import logoutService from './logoutService.js';
import navigationService from './navigationService.js';
import userActivityService from './userActivityService.js';
import enhancedNotificationService from './enhancedNotificationService.js';

class AppInitService {
  constructor() {
    this.isInitialized = false;
    this.initPromise = null;
  }

  /**
   * Initialize the entire application
   */
  async initialize() {
    if (this.isInitialized) {
      console.log('🚀 App already initialized');
      return;
    }

    if (this.initPromise) {
      console.log('🚀 App initialization in progress, waiting...');
      return await this.initPromise;
    }

    this.initPromise = this.performInitialization();
    return await this.initPromise;
  }

  /**
   * Perform the actual initialization
   */
  async performInitialization() {
    console.log('🚀 Starting application initialization...');

    try {
      // Step 1: Initialize navigation service
      console.log('📍 Initializing navigation service...');
      navigationService.initialize();

      // Step 1.5: Initialize user activity service
      console.log('👥 Initializing user activity service...');
      await userActivityService.initialize();

      // Step 1.6: Initialize enhanced notification service
      console.log('📢 Initializing enhanced notification service...');
      await enhancedNotificationService.initialize();

      // Step 2: Set up logout listeners
      console.log('🔐 Setting up logout listeners...');
      this.setupLogoutListeners();

      // Step 3: Set up navigation listeners
      console.log('📍 Setting up navigation listeners...');
      this.setupNavigationListeners();

      // Step 4: Set up error handlers
      console.log('❌ Setting up error handlers...');
      this.setupErrorHandlers();

      // Step 5: Set up beforeunload handler
      console.log('🚪 Setting up beforeunload handler...');
      this.setupBeforeUnloadHandler();

      // Step 6: Set up visibility change handler
      console.log('👁️ Setting up visibility change handler...');
      this.setupVisibilityChangeHandler();

      this.isInitialized = true;
      console.log('✅ Application initialization completed successfully');

    } catch (error) {
      console.error('❌ Application initialization failed:', error);
      throw error;
    }
  }

  /**
   * Set up logout listeners
   */
  setupLogoutListeners() {
    // Listen for logout events from any component
    logoutService.addLogoutListener(() => {
      console.log('🔐 Global logout detected, cleaning up application state...');
      
      // Clean up data loading service
      dataLoadingService.reset();

      // Clean up navigation service
      navigationService.cleanup();

      // Clean up enhanced notification service
      enhancedNotificationService.cleanup();
    });

    // Listen for authentication errors that should trigger logout
    window.addEventListener('authError', (event) => {
      console.log('🔐 Authentication error detected, triggering logout:', event.detail);
      logoutService.logout({
        reason: 'auth_error',
        skipRedirect: false
      });
    });
  }

  /**
   * Set up navigation listeners
   */
  setupNavigationListeners() {
    navigationService.addNavigationListener((navigationData) => {
      console.log('📍 Navigation change detected in app init service:', navigationData);
      
      // Handle specific navigation scenarios
      const { newPath, newParams } = navigationData;
      
      // If navigating to login page, clean up everything
      if (newPath === '/login' || newPath === '/register') {
        console.log('📍 Navigating to auth page, cleaning up...');
        dataLoadingService.reset();
      }
      
      // If navigating away from auth pages, ensure services are ready
      if (navigationData.oldPath === '/login' || navigationData.oldPath === '/register') {
        console.log('📍 Navigating from auth page, reinitializing services...');
        // Small delay to ensure authentication state is updated
        setTimeout(() => {
          this.reinitializeAfterAuth();
        }, 500);
      }
    });
  }

  /**
   * Set up global error handlers
   */
  setupErrorHandlers() {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      console.error('🚨 Unhandled promise rejection:', event.reason);
      
      // Check if it's an authentication error
      if (event.reason?.message?.includes('401') || 
          event.reason?.message?.includes('Unauthorized')) {
        console.log('🔐 Authentication error in unhandled rejection, triggering logout');
        logoutService.logout({
          reason: 'unhandled_auth_error',
          skipRedirect: false
        });
      }
    });

    // Handle global errors
    window.addEventListener('error', (event) => {
      console.error('🚨 Global error:', event.error);
      
      // Log error for debugging but don't trigger logout unless it's auth-related
      if (event.error?.message?.includes('401') || 
          event.error?.message?.includes('Unauthorized')) {
        console.log('🔐 Authentication error in global error, triggering logout');
        logoutService.logout({
          reason: 'global_auth_error',
          skipRedirect: false
        });
      }
    });
  }

  /**
   * Set up beforeunload handler
   */
  setupBeforeUnloadHandler() {
    window.addEventListener('beforeunload', (event) => {
      console.log('🚪 Page unloading, cleaning up...');
      
      // Clean up services
      dataLoadingService.reset();
      
      // Don't prevent unload, just clean up
    });
  }

  /**
   * Set up visibility change handler
   */
  setupVisibilityChangeHandler() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Page became visible, refreshing data...');
        
        // Refresh data when page becomes visible again
        setTimeout(() => {
          dataLoadingService.refreshData();
        }, 1000);
      } else {
        console.log('👁️ Page became hidden');
      }
    });
  }

  /**
   * Reinitialize services after authentication
   */
  async reinitializeAfterAuth() {
    console.log('🔄 Reinitializing services after authentication...');
    
    try {
      // Clear any stale data
      dataLoadingService.invalidateCache();
      
      // Reinitialize activity service
      await userActivityService.initialize();
      
      // Reinitialize notification service
      await enhancedNotificationService.initialize();
      
      // Refresh current page data
      const currentPath = window.location.pathname;
      const currentParams = new URLSearchParams(window.location.search);
      
      dataLoadingService.handleNavigationChange(currentPath, Object.fromEntries(currentParams));
      
      console.log('✅ Services reinitialized after authentication');
    } catch (error) {
      console.error('❌ Failed to reinitialize services after authentication:', error);
    }
  }

  /**
   * Clean up the application
   */
  cleanup() {
    console.log('🧹 Cleaning up application...');
    
    // Clean up all services
    dataLoadingService.reset();
    navigationService.cleanup();
    enhancedNotificationService.cleanup();
    
    this.isInitialized = false;
    this.initPromise = null;
    
    console.log('✅ Application cleanup completed');
  }

  /**
   * Check if app is initialized
   */
  isAppInitialized() {
    return this.isInitialized;
  }

  /**
   * Force refresh all data
   */
  async refreshAllData() {
    console.log('🔄 Force refreshing all application data...');
    
    try {
      await dataLoadingService.refreshData();
      console.log('✅ All data refreshed successfully');
    } catch (error) {
      console.error('❌ Failed to refresh all data:', error);
    }
  }

  /**
   * Handle authentication state changes
   */
  handleAuthStateChange(isAuthenticated, user = null) {
    console.log('🔐 Authentication state changed:', { isAuthenticated, user: user?.email });
    
    if (!isAuthenticated) {
      // User logged out, clean up
      this.cleanup();
    } else {
      // User logged in, reinitialize if needed
      if (!this.isInitialized) {
        this.initialize();
      } else {
        this.reinitializeAfterAuth();
      }
    }
  }
}

// Create singleton instance
const appInitService = new AppInitService();

export default appInitService;
